import ChipList from '../common/ChipList';
import type { NewsBeatAttribute, NewsAttributeRole } from '../../types';

/**
 * The beat's custom attributes — the axes its stories are judged and sorted on.
 *
 * The ROLE control is the load-bearing part of this screen, and the copy under
 * each option is doing real work. A user who types `state: UT, NV, CO` almost
 * always means "only these states", while `topic: legislation, outages` almost
 * always means "tell me which one". Getting that backwards is invisible until a
 * briefing comes back empty (everything treated as a filter) or unsortable
 * (nothing filtered), so the two options say what they DO to an article rather
 * than naming a concept.
 */

const ROLES: { value: NewsAttributeRole; label: string; blurb: string }[] = [
  {
    value: 'FILTER',
    label: 'Filter',
    blurb: "Drops articles that don't match. Use for scope.",
  },
  {
    value: 'TAG',
    label: 'Tag',
    blurb: 'Labels only, never drops. Use for sorting.',
  },
];

export default function AttributeEditor({
  attributes,
  onChange,
  beatName,
  beatDescription,
  disabled = false,
}: {
  attributes: NewsBeatAttribute[];
  onChange: (next: NewsBeatAttribute[]) => void;
  beatName?: string;
  beatDescription?: string | null;
  disabled?: boolean;
}) {
  const patch = (index: number, changes: Partial<NewsBeatAttribute>) => {
    onChange(
      attributes.map((a, i) => (i === index ? { ...a, ...changes } : a)),
    );
  };

  const add = () => {
    onChange([
      ...attributes,
      {
        name: '',
        // TAG by default: a mis-set tag makes a story badly sorted, a mis-set
        // filter makes it disappear. Default to the recoverable mistake.
        role: 'TAG',
        values: [],
        hint: '',
        sort_order: attributes.length,
      },
    ]);
  };

  const remove = (index: number) => {
    onChange(attributes.filter((_, i) => i !== index));
  };

  const context = {
    beat_name: beatName ?? '',
    beat_description: beatDescription ?? '',
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Attributes
        </h3>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          Ways of classifying a story. Filters decide what gets in; tags decide
          how the briefing is grouped and sorted.
        </p>
      </div>

      {attributes.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
          No attributes. Relevance will be judged from the beat's description and
          search terms alone.
        </p>
      )}

      {attributes.map((attribute, index) => (
        <div
          key={index}
          className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3"
        >
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Name
              </label>
              <input
                value={attribute.name}
                disabled={disabled}
                onChange={(e) => patch(index, { name: e.target.value })}
                placeholder="e.g. state"
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-1.5 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => remove(index)}
              disabled={disabled}
              className="mt-6 text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
            >
              Remove
            </button>
          </div>

          <fieldset>
            <legend className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              What should it do?
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {ROLES.map((role) => (
                <label
                  key={role.value}
                  className={`flex gap-2 items-start rounded-md border p-2 cursor-pointer ${
                    attribute.role === role.value
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    className="mt-0.5"
                    name={`role-${index}`}
                    checked={attribute.role === role.value}
                    disabled={disabled}
                    onChange={() => patch(index, { role: role.value })}
                  />
                  <span>
                    <span className="block text-sm text-gray-900 dark:text-gray-100">
                      {role.label}
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      {role.blurb}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Allowed values
            </label>
            <ChipList
              values={attribute.values}
              onChange={(values) => patch(index, { values })}
              placeholder="e.g. UT - Enter to add"
              lowercase
              clearable
              disabled={disabled}
              suggestContext="news_attribute_value"
              suggestLabel="Values"
              additionalContext={{
                ...context,
                attribute_name: attribute.name,
              }}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {attribute.values.length === 0
                ? attribute.role === 'FILTER'
                  ? 'No values - relevance will be judged from the hint below, so write a clear one.'
                  : 'No values - the agent will choose its own labels.'
                : 'Stories are labeled with one of these.'}
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Hint <span className="font-normal">(optional)</span>
            </label>
            <input
              value={attribute.hint ?? ''}
              disabled={disabled}
              onChange={(e) => patch(index, { hint: e.target.value })}
              placeholder="e.g. Which US state the story affects"
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-1.5 text-sm"
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={add}
        disabled={disabled}
        className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-50"
      >
        + Add attribute
      </button>
    </div>
  );
}
